<script setup lang="ts">
import { DateTime } from "luxon";
import { computed, ref, watch } from "vue";
import { ArrowPathIcon, ArrowDownTrayIcon } from "@heroicons/vue/20/solid";
import { useRoute } from "vue-router";
import Alipay from "../assets/images/payments/alipay.svg";
import Amex from "../assets/images/payments/amex.svg";
import Discover from "../assets/images/payments/discover.svg";
import JCB from "../assets/images/payments/jcb.svg";
import Mastercard from "../assets/images/payments/mastercard.svg";
import UnionPay from "../assets/images/payments/unionpay.svg";
import Visa from "../assets/images/payments/visa.svg";
import WechatPay from "../assets/images/payments/wechatpay.svg";
import { useLoading } from "../composables/useLoading";
import { useNotification } from "../composables/useNotification";
import { UserStore, useStores } from "../composables/useStores";
import { FirestoreService } from "../services/firestore";
import { isRootAdmin } from "../services/permissions";
import { Payment } from "../types/payment";
import EftposAustralia from "../assets/images/payments/eftpos.png";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import JsonCsv from "vue-json-csv";
import Button from "../components/Common/Button.vue";
import CustomInput from "../components/Common/CustomInput.vue";
import axios from "axios";
import { Store } from "../types/store";
import { useCurrentUser } from "vuefire";
const { setText, toggleLoading } = useLoading();
const { setError, setSuccess } = useNotification();
const { currentStore } = useStores();
const user = useCurrentUser();
const refundAmount = ref<{ [key: string]: number }>({});
const payments = ref<Payment[]>([]);

// Unsuccessful notifications (declines/refusals/failed modifications), recorded
// by the backend `other-payment` aggregate. Admin-only.
type OtherPayment = {
  id: string;
  psp?: string;
  merchantReference?: string;
  merchantAccountCode?: string;
  date?: string;
  amount?: { amount: number };
  paymentMethod?: string;
  eventCode?: string;
  reason?: string;
  refusalReason?: string;
  refusalReasonCode?: string;
  originalReference?: string;
};
const otherPayments = ref<OtherPayment[]>([]);
const view = ref<"payments" | "other">("payments");
const isAdmin = computed(() => isRootAdmin(user.value?.uid));

const init = async (store: UserStore, date: string) => {
  setText("Loading payments...");
  toggleLoading(true);
  try {
    const base = `/users/${store.user}/merchants/${store.merchant}/stores/${store.store}/days/${date}`;
    payments.value = await FirestoreService.getCollection<Payment>(
      `${base}/payments`,
    );
    // Other (unsuccessful) payments — only read for the admin view.
    if (isAdmin.value) {
      try {
        otherPayments.value = await FirestoreService.getCollection<OtherPayment>(
          `${base}/other-payments`,
        );
      } catch {
        otherPayments.value = [];
      }
    } else {
      otherPayments.value = [];
    }
  } catch (e) {
    console.log(e);
    setError("failed to load payments please try again");
  } finally {
    toggleLoading(false);
  }
};
const route = useRoute();
const currentDate = ref(route.params.date as string);
watch(
  () => [currentDate.value, currentStore.value?.store],
  async ([dateVal, storeId]) => {
    if (!dateVal || !storeId) return;
    await init(currentStore.value!, dateVal);
  },
  { immediate: true },
);
const sortedPayments = computed(() =>
  [...payments.value].sort((p1, p2) =>
    DateTime.fromISO(p1.date).toMillis() < DateTime.fromISO(p2.date).toMillis()
      ? 1
      : -1,
  ),
);
const sortedOther = computed(() =>
  [...otherPayments.value].sort((a, b) =>
    DateTime.fromISO(a.date ?? "").toMillis() <
    DateTime.fromISO(b.date ?? "").toMillis()
      ? 1
      : -1,
  ),
);

const paymentMethodToImage = (paymentMethod: string) => {
  switch (paymentMethod.toLowerCase()) {
    case "visa":
      return Visa;
    case "alipay":
      return Alipay;
    case "mc":
      return Mastercard;
    case "jcb":
      return JCB;
    case "amex":
      return Amex;
    case "discover":
      return Discover;
    case "cup":
      return UnionPay;
    case "wechatpay":
      return WechatPay;
    case "eftpos_australia":
      return EftposAustralia;
    default:
      return;
  }
};
const zone = (store: Store) => {
  if (store.address?.state === "VIC") {
    return "Australia/Melbourne";
  }
  return "Australia/Brisbane";
};
// const dateUpdated = async (date: Date) => {
//   const x = DateTime.fromISO(date.toISOString())
//     .setZone(zone(currentStore.value!.data))
//     .toFormat("yyyy-MM-dd");
//   console.log({ date, x });
//   if (!currentStore.value) return;
//   await init(currentStore.value, x);
// };

const canRefund = computed(() => {
  if (!user.value) return false;
  const uid = user.value.uid;
  const eligibleUids = [
    "DLlwcjH2syWG1glCmQXTcOu9HqE3",
    "htxFJFQf8MhpnKWanU227XNAKui2",
    "gGOcf8wxqSMb6Yo6fZvIGMtqMku2",
    "7hQIPKc1cIPJT7k9S26jna5YPay1",
    "BSWdP7v0nwdLu3zXpBGfG9J7J8d2",
    "gLjwSfE25yaM249PLEsD3H2nJJJ2",
    "ZVZmopSK6CXgf9OmdD2Vz1LPuIj2", //melanie
    "MBZ8s9hJQOfRLh0rR0WOj8DYThf2",
    "uzkYGufqR2UQeuApdHb22fzcE2Z2", //underwood
    "VOXq0R185Xf9Avoebm93qdjdGmc2", //herston
    "3W5jfqTDAcP0Vddfd5zx3kB0tQn1", //burleigh
    "aloqCbJXnpSxkmR8TOmWaxxor0B3", //gold coast
    "D82ZVX4cyFaCmOuGWb17K3utTxI2", //outlet
    "vylhgZcWD5gMOmdZGBhWujBLN0A2", //rebecca
    "tZK4uTwZenSryJuQZoaPS1rhQoS2", //tenille
  ];
  return eligibleUids.includes(uid);
});
const downloadable = computed(() => {
  if (!payments.value) return;
  if (payments.value.length === 0) return;
  if (!currentStore.value) return;
  return payments.value.map((payment) => {
    return {
      Date: DateTime.fromISO(payment.date)
        .setZone(zone(currentStore.value!.data))
        .toFormat("yyyy-MM-dd HH:mm:ss"),
      Total: (payment.amount.amount ?? 0) / 100,
      Surcharge: Payment.surcharge(payment) / 100,
      Fees: (Payment.commission(payment) ?? 0) / 100,
      Net: (Payment.net(payment) ?? 0) / 100,
      PaymentMethod: payment.paymentMethod,
      Status: payment.refundTotal ? "Refunded" : "Authorised",
      ID:
        payment.merchantReference &&
        payment.merchantReference.startsWith("payopbl")
          ? `${payment.merchantReference} (Remote)`
          : payment.merchantReference ?? "N/A",
    };
  });
});

const initiateRefund = async (payment: Payment) => {
  const result = confirm(
    `Are you sure you want to initiate a refund  of \$${
      refundAmount.value[payment.psp]
    } for this payment?`,
  );
  if (!result) return;
  if (!user.value) {
    setError("You must be logged in to perform a refund");
    return;
  }
  const token = await user.value.getIdToken();
  try {
    setText("Initiating refund...");
    toggleLoading(true);
    axios.post(
      "https://api.production.terminal.payo.com.au/create-ecom-refund",
      {
        amount: refundAmount.value[payment.psp],
        payment: payment.psp,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
    setSuccess("Refund initiated successfully");
  } catch (e) {
    if (axios.isAxiosError(e)) {
      setError(e.response?.data?.message || "Failed to initiate refund");
    } else {
      setError("An unexpected error occurred while initiating the refund");
    }
  } finally {
    toggleLoading(false);
  }
};
</script>
<template>
  <div class="px-4 pb-4 sm:pb-6 sm:px-6 lg:px-8 lg:pb-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <div class="sm:flex sm:items-center mb-6">
          <div
            class="sm:flex-auto flex flex-row justify-start space-x-2 items-center"
          >
            <h1 class="text-[#111827] font-poppins font-semibold text-2xl">
              Payments
            </h1>
            <arrow-path-icon
              class="w-6 h-6 text-black block cursor-pointer"
              @click="currentStore && init(currentStore, currentDate)"
            />
            <json-csv
              :data="downloadable"
              v-if="downloadable && currentStore"
              :name="`${currentStore.data.description}-payments-${currentDate}`"
            >
              <arrow-down-tray-icon
                class="w-6 h-6 text-black block cursor-pointer"
              />
            </json-csv>
          </div>
        </div>
        <div class="w-[200px] max-w-full my-4">
          <VueDatePicker
            v-model="currentDate"
            format="yyyy-MM-dd"
            model-type="yyyy-MM-dd"
            :enable-time-picker="false"
          />
        </div>
      </div>
    </div>
    <div class="p-4 bg-white rounded-[16px]">
      <!-- Admin only: switch between successful payments and other
           (unsuccessful: declines/refusals/failed modifications) payments. -->
      <div v-if="isAdmin" class="mb-2 flex gap-2">
        <button
          type="button"
          class="rounded-md px-3 py-1.5 text-sm font-medium"
          :class="
            view === 'payments'
              ? 'bg-black text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="view = 'payments'"
        >
          Successful ({{ payments.length }})
        </button>
        <button
          type="button"
          class="rounded-md px-3 py-1.5 text-sm font-medium"
          :class="
            view === 'other'
              ? 'bg-black text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          "
          @click="view = 'other'"
        >
          Other / unsuccessful ({{ otherPayments.length }})
        </button>
      </div>
      <div class="-mx-4 mt-8 sm:-mx-0">
        <table
          class="min-w-full"
          v-if="currentStore && view === 'payments'"
        >
          <thead class="border-none">
            <tr class="border-none">
              <th
                scope="col"
                class="rounded-l-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Date
              </th>

              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Total
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Surcharge
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Fees
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Net
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Payment Method
              </th>
              <th
                scope="col"
                class="rounded-r-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Status
              </th>
              <th
                scope="col"
                class="rounded-r-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                ID
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="payment in sortedPayments" :key="payment.id">
              <td
                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0"
              >
                {{
                  DateTime.fromISO(payment.date)
                    .setZone(zone(currentStore.data))
                    .toFormat("yyyy-MM-dd HH:mm:ss")
                }}
                <dl class="font-normal lg:hidden">
                  <dt class="sr-only">Amount</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    {{
                      (payment.amount.amount / 100).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }}
                  </dd>
                </dl>
              </td>

              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  (Payment.total(payment) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  (Payment.surcharge(payment) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  (Payment.commission(payment) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  (Payment.net(payment) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                <div class="flex flex-row justify items-center">
                  <img
                    :src="paymentMethodToImage(payment.paymentMethod)"
                    v-if="paymentMethodToImage(payment.paymentMethod)"
                    class="block wx-auto w-[50px] h-auto"
                  />

                  <p v-else>{{ payment.paymentMethod }}</p>
                </div>
              </td>
              <td class="px-3 py-4 text-sm text-gray-500">
                {{
                  payment.refundTotal
                    ? `refunded (${(
                        payment.refundTotal.amount / 100
                      ).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })})`
                    : payment.status
                }}
                <div class="max-w-[150px]">
                  <custom-input
                    type="number"
                    required
                    label="Amount to refund"
                    v-model="refundAmount[payment.psp]"
                    class="mb-2"
                    v-if="canRefund"
                  />
                  <Button
                    label="Refund"
                    @click="initiateRefund(payment)"
                    v-if="canRefund"
                  />
                </div>
              </td>
              <td
                class="px-3 py-4 text-sm text-gray-500"
                v-if="currentStore.store === 'store.ST329SZ22322925N868N2GLVK'"
              >
                {{ payment.merchantReference }}
              </td>
              <td class="px-3 py-4 text-sm text-gray-500" v-else>
                {{
                  payment.merchantReference &&
                  payment.merchantReference.startsWith("payopbl")
                    ? `${payment.merchantReference} (Remote)`
                    : payment.merchantReference ?? "N/A"
                }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Other / unsuccessful payments (admin only) -->
        <table
          class="min-w-full"
          v-else-if="currentStore && view === 'other'"
        >
          <thead class="border-none">
            <tr class="border-none">
              <th
                scope="col"
                class="rounded-l-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Date
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Amount
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Method
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Event
              </th>
              <th
                scope="col"
                class="table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Reason
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Reference
              </th>
              <th
                scope="col"
                class="rounded-r-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                PSP
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="p in sortedOther" :key="p.id">
              <td
                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0"
              >
                {{
                  p.date
                    ? DateTime.fromISO(p.date)
                        .setZone(zone(currentStore.data))
                        .toFormat("yyyy-MM-dd HH:mm:ss")
                    : "—"
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  ((p.amount?.amount ?? 0) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                <div class="flex flex-row items-center">
                  <img
                    :src="paymentMethodToImage(p.paymentMethod ?? '')"
                    v-if="paymentMethodToImage(p.paymentMethod ?? '')"
                    class="block w-[50px] h-auto"
                  />
                  <p v-else>{{ p.paymentMethod ?? "—" }}</p>
                </div>
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{ p.eventCode ?? "—" }}
              </td>
              <td class="px-3 py-4 text-sm text-gray-500">
                {{ p.refusalReason ?? p.reason ?? "—"
                }}<span v-if="p.refusalReasonCode" class="text-gray-400">
                  ({{ p.refusalReasonCode }})</span
                >
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{ p.merchantReference ?? "N/A" }}
              </td>
              <td class="px-3 py-4 text-sm text-gray-500 break-all">
                {{ p.psp ?? "—" }}
              </td>
            </tr>
            <tr v-if="sortedOther.length === 0">
              <td
                colspan="7"
                class="px-3 py-6 text-center text-sm text-gray-500"
              >
                No other payments for this date.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
